/**
 * AccountantModules.js
 * Three stand-alone tabs rendered inside AccountantPage:
 *   • AssetsRegisterTab  — Fixed & Current asset register with depreciation
 *   • HotelReportsTab    — All hotel-specific reports with branded PDF/Excel export
 *   • SpoilageReviewTab  — Accountant view of submitted spoilage reports
 */
import React, { useState, useEffect, useCallback } from 'react';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import { StatCards, Card, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';
import axiosClient from '../api/client';
import { getBrandingSlug } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Accountant Approval Stamp (used in SpoilageReviewTab) ───────────────────
function SpoilageAccountantStamp({ by, at, size = 72 }) {
  const cx = size / 2, cy = size / 2;
  const rOuter = cx - 3, rInner = cx - 10;
  const arcR = rOuter - 5;
  const uid = `sp_acct_${size}`;
  const mainFS = Math.round(9 * 0.82 * (size / 72));
  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity:0.9 }}>
        <defs>
          <path id={`${uid}_top`} d={`M ${cx-arcR},${cy} a ${arcR},${arcR} 0 1,1 ${arcR*2},0`}/>
          <filter id={`${uid}_d`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="0.9" xChannelSelector="R" yChannelSelector="G"/>
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={rOuter} fill="#fdf6ec" stroke="#8B1A1A" strokeWidth="2.2" filter={`url(#${uid}_d)`}/>
        <circle cx={cx} cy={cy} r={rInner} fill="none"    stroke="#8B1A1A" strokeWidth="1.2" filter={`url(#${uid}_d)`}/>
        <text fontSize={Math.max(5,size*0.077)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="800" letterSpacing="1.4">
          <textPath href={`#${uid}_top`} startOffset="15%">GRAND MEMORIES</textPath>
        </text>
        <line x1={cx-rInner+5} y1={cy-rInner*0.40} x2={cx+rInner-5} y2={cy-rInner*0.40} stroke="#8B1A1A" strokeWidth="0.9"/>
        <line x1={cx-rInner+5} y1={cy+rInner*0.20} x2={cx+rInner-5} y2={cy+rInner*0.20} stroke="#8B1A1A" strokeWidth="0.9"/>
        <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" fontSize={mainFS}
          fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="900" filter={`url(#${uid}_d)`}>ACCOUNTANT</text>
        <text x={cx} y={cy+rInner*0.38} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4.5,size*0.069)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="600" letterSpacing="1.8">— APPROVED —</text>
        <text x={cx-rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx+rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx} y={cy+rInner*0.85} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4,size*0.069)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="700" letterSpacing="1.6">RESORT &amp; SPA</text>
      </svg>
      {(by||at) && (
        <div style={{ fontSize:9, color:'#888', textAlign:'center', maxWidth:size, lineHeight:1.3 }}>
          {by && <div style={{ fontWeight:600 }}>{by}</div>}
          {at && <div>{new Date(at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'})}</div>}
        </div>
      )}
    </div>
  );
}

/* ─── Shared helpers ─────────────────────────────────────────────────────────── */
const row = (label, value, accent = false) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #1e1e1e' }}>
    <span style={{ color: 'var(--textMuted)', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: accent ? 700 : 500, color: accent ? '#c8a96e' : 'var(--text)', fontSize: 13 }}>{value}</span>
  </div>
);

function SectionHead({ title }) {
  return (
    <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 14, margin: '20px 0 10px', paddingBottom: 6, borderBottom: '2px solid #c8a96e33' }}>
      {title}
    </div>
  );
}

async function doDownload(url, filename) {
  try {
    const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '', timeout: 120000 });
    // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
    const blob = res.data;
    if (blob.size < 50) throw new Error('Empty file returned — no data for this period');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename || url.split('/').filter(Boolean).pop().split('?')[0] || 'report';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
  } catch(e) {
    const msg = e.name === 'TimeoutError' ? 'Export timed out — try a shorter date range' : await readBlobError(e);
    alert(`Export failed: ${msg}`);
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   ASSETS REGISTER TAB
   ═══════════════════════════════════════════════════════════════════════════════ */

const ASSET_CATS = [
  { v: 'land',          l: 'Land & Property',              type: 'fixed' },
  { v: 'building',      l: 'Buildings & Structures',        type: 'fixed' },
  { v: 'furniture',     l: 'Furniture & Fittings',          type: 'fixed' },
  { v: 'equipment',     l: 'Kitchen & Restaurant Equipment', type: 'fixed' },
  { v: 'it_equipment',  l: 'IT Equipment & Systems',        type: 'fixed' },
  { v: 'vehicles',      l: 'Vehicles & Transport',          type: 'fixed' },
  { v: 'hvac',          l: 'HVAC & Utilities',              type: 'fixed' },
  { v: 'laundry_eq',    l: 'Laundry Equipment',             type: 'fixed' },
  { v: 'pool_eq',       l: 'Pool & Recreation Equipment',   type: 'fixed' },
  { v: 'other_fixed',   l: 'Other Fixed Asset',             type: 'fixed' },
  { v: 'inventory',     l: 'Inventory / Stock',             type: 'current' },
  { v: 'cash',          l: 'Cash & Bank',                   type: 'current' },
  { v: 'receivables',   l: 'Accounts Receivable',           type: 'current' },
  { v: 'prepaid',       l: 'Prepaid Expenses',              type: 'current' },
  { v: 'other_current', l: 'Other Current Asset',           type: 'current' },
];

const ASSET_STATUS = ['active', 'under_repair', 'idle', 'disposed'];
const DEP_METHODS  = [
  { v: 'straight_line',    l: 'Straight-Line (SLM)' },
  { v: 'reducing_balance', l: 'Reducing Balance (RBM)' },
  { v: 'none',             l: 'Not Depreciated' },
];
const STATUS_COLOR = { active: '#2ecc71', under_repair: '#f39c12', idle: '#888', disposed: '#e74c3c' };

const blankAsset = {
  name: '', description: '', asset_type: 'fixed', category: 'furniture',
  location: '', serial_number: '', supplier: '', invoice_number: '',
  purchase_date: '', purchase_cost: '', useful_life_years: '5',
  salvage_value: '0', depreciation_method: 'straight_line',
  depreciation_rate: '0', status: 'active', insurance_policy: '',
  warranty_expiry: '', notes: '',
};

export function AssetsRegisterTab({ notify }) {
  const [assets,   setAssets]   = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [showModal,setShowModal]= useState(false);
  const [editAsset,setEditAsset]= useState(null);
  const [form,     setForm]     = useState({ ...blankAsset });
  const [saving,   setSaving]   = useState(false);
  const [exporting,setExporting]= useState(false);
  const [filterType,    setFilterType]    = useState('');
  const [filterStatus,  setFilterStatus]  = useState('active');
  const [filterCategory,setFilterCategory]= useState('');
  const [search,        setSearch]        = useState('');
  const [viewAsset,     setViewAsset]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let params = '?';
      if (filterType)     params += `asset_type=${filterType}&`;
      if (filterStatus)   params += `status=${filterStatus}&`;
      if (filterCategory) params += `category=${filterCategory}&`;
      if (search)         params += `search=${encodeURIComponent(search)}&`;
      const [a, s] = await Promise.all([api.getAssets(params), api.getAssetSummary()]);
      setAssets(a.data.results || a.data);
      setSummary(s.data);
    } catch (e) { notify('Failed to load assets', 'error'); }
    setLoading(false);
  }, [filterType, filterStatus, filterCategory, search, notify]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditAsset(null);
    setForm({ ...blankAsset, purchase_date: new Date().toISOString().split('T')[0] });
    setShowModal(true);
  };

  const openEdit = (a) => {
    setEditAsset(a);
    setForm({
      name: a.name, description: a.description || '', asset_type: a.asset_type,
      category: a.category, location: a.location || '', serial_number: a.serial_number || '',
      supplier: a.supplier || '', invoice_number: a.invoice_number || '',
      purchase_date: a.purchase_date, purchase_cost: String(a.purchase_cost),
      useful_life_years: String(a.useful_life_years), salvage_value: String(a.salvage_value),
      depreciation_method: a.depreciation_method, depreciation_rate: String(a.depreciation_rate),
      status: a.status, insurance_policy: a.insurance_policy || '',
      warranty_expiry: a.warranty_expiry || '', notes: a.notes || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name || !form.purchase_date || !form.purchase_cost) {
      notify('Name, date and cost are required', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_cost: parseFloat(form.purchase_cost),
        useful_life_years: parseFloat(form.useful_life_years) || 5,
        salvage_value: parseFloat(form.salvage_value) || 0,
        depreciation_rate: parseFloat(form.depreciation_rate) || 0,
        warranty_expiry: form.warranty_expiry || null,
      };
      if (editAsset) await api.updateAsset(editAsset.id, payload);
      else           await api.createAsset(payload);
      notify(editAsset ? 'Asset updated' : 'Asset added', 'success');
      setShowModal(false);
      load();
    } catch (e) { notify('Save failed', 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try { await api.deleteAsset(id); notify('Deleted', 'info'); load(); }
    catch (e) { notify('Delete failed', 'error'); }
  };

  const exportPdf = async () => {
    setExporting(true);
    doDownload(`${BACKEND_URL}/api/assets/register/export-pdf/`);
    setTimeout(() => setExporting(false), 3000);
  };

  const fmtCur = (v) => `KES ${parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
  const cardSt = { background: 'var(--bgCard)', border: '1px solid #1e1e1e', borderRadius: 6, padding: 14, marginBottom: 10 };

  return (
    <div>
      {/* Summary */}
      {summary && (
        <StatCards stats={[
          { label: 'Total Assets',    value: summary.total_assets,                             color: '#3498db', icon: '🏗️' },
          { label: 'Total Cost',      value: fmtKES(summary.total_cost),                       color: '#c8a96e', icon: '💰' },
          { label: 'Net Book Value',  value: fmtKES(summary.total_nbv),                        color: '#2ecc71', icon: '📊' },
          { label: 'Annual Dep. Charge', value: fmtKES(summary.annual_depreciation_charge),    color: '#e74c3c', icon: '📉' },
        ]} />
      )}

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12, marginBottom: 16 }}>
          <div style={{ ...cardSt }}>
            {row('Fixed Assets (count)', summary.fixed_assets_count)}
            {row('Fixed Assets NBV', fmtCur(summary.fixed_assets_nbv), true)}
            {row('Current Assets (count)', summary.current_assets_count)}
            {row('Current Assets NBV', fmtCur(summary.current_assets_nbv), true)}
            {row('Disposed Assets', summary.disposed_count)}
          </div>
          <div style={{ ...cardSt }}>
            <div style={{ fontWeight: 700, color: '#c8a96e', marginBottom: 8, fontSize: 12 }}>BY CATEGORY</div>
            {(summary.by_category || []).map(c => (
              <div key={c.category} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #1a1a1a', fontSize: 12 }}>
                <span style={{ color: 'var(--textMuted)' }}>{c.category}</span>
                <span style={{ color: '#c8a96e', fontWeight: 600 }}>{fmtCur(c.nbv)} <span style={{ color: '#555', fontSize: 11 }}>({c.count})</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <select style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            <option value="fixed">Fixed Assets</option>
            <option value="current">Current Assets</option>
          </select>
          <select style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            {ASSET_STATUS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {ASSET_CATS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
          <input style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, width: 160 }} placeholder="Search assets…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...btnSm, background: '#8B2635' }} onClick={exportPdf} disabled={exporting}>
            {exporting ? '⏳…' : '📄 Export PDF'}
          </button>
          <button style={btnPrimary} onClick={openNew}>+ Add Asset</button>
        </div>
      </div>

      {/* Table */}
      {loading ? <div style={{ color: 'var(--textMuted)', padding: 24, textAlign: 'center' }}>Loading…</div>
        : assets.length === 0 ? <Empty text="No assets found" />
        : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#0d0d0d' }}>
                  {['Code', 'Name', 'Type / Category', 'Location', 'Purchase Date', 'Cost (KES)', 'Acc. Dep.', 'NBV (KES)', 'Dep. Method', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#c8a96e', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid #2a2a2a' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assets.map((a, idx) => (
                  <tr key={a.id} style={{ background: idx % 2 === 0 ? 'transparent' : '#0a0a0a', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#111'}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : '#0a0a0a'}>
                    <td style={{ padding: '7px 10px', color: '#c8a96e', fontWeight: 700 }}>{a.asset_code}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 600 }} onClick={() => setViewAsset(a)}>{a.name}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--textMuted)' }}>
                      <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: a.asset_type === 'fixed' ? '#3498db' : '#9b59b6' }}>{a.asset_type === 'fixed' ? 'Fixed' : 'Current'}</span>
                      {a.category_label}
                    </td>
                    <td style={{ padding: '7px 10px', color: 'var(--textMuted)' }}>{a.location || '—'}</td>
                    <td style={{ padding: '7px 10px' }}>{a.purchase_date}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{parseFloat(a.purchase_cost).toLocaleString()}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: '#e74c3c' }}>{parseFloat(a.accumulated_depreciation || 0).toLocaleString()}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: '#2ecc71' }}>{parseFloat(a.net_book_value || 0).toLocaleString()}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--textMuted)', fontSize: 11 }}>{a.depreciation_method.replace('_', ' ')}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: 'rgba(0,0,0,0.3)', color: STATUS_COLOR[a.status] || '#888', border: `1px solid ${STATUS_COLOR[a.status] || '#888'}` }}>
                        {a.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '7px 6px', whiteSpace: 'nowrap' }}>
                      <button style={{ ...btnSm, padding: '3px 8px', fontSize: 11, marginRight: 4 }} onClick={() => openEdit(a)}>✏️</button>
                      <button style={{ ...btnSm, padding: '3px 8px', fontSize: 11, background: '#3a0a0a' }} onClick={() => del(a.id)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* View Detail Modal */}
      {viewAsset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--bgCard)', border: '1px solid #2a2a2a', borderRadius: 8, padding: 24, maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 16, marginBottom: 4 }}>{viewAsset.asset_code} — {viewAsset.name}</div>
            <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 16 }}>{viewAsset.category_label} · {viewAsset.asset_type_label}</div>
            <SectionHead title="📋 Details" />
            {row('Location', viewAsset.location || '—')}
            {row('Serial Number', viewAsset.serial_number || '—')}
            {row('Supplier', viewAsset.supplier || '—')}
            {row('Invoice No.', viewAsset.invoice_number || '—')}
            {row('Purchase Date', viewAsset.purchase_date)}
            {row('Warranty Expiry', viewAsset.warranty_expiry || '—')}
            {row('Insurance Policy', viewAsset.insurance_policy || '—')}
            {row('Status', viewAsset.status.replace('_', ' '))}
            <SectionHead title="💰 Valuation & Depreciation" />
            {row('Purchase Cost', fmtCur(viewAsset.purchase_cost), true)}
            {row('Salvage Value', fmtCur(viewAsset.salvage_value))}
            {row('Useful Life', `${viewAsset.useful_life_years} years`)}
            {row('Depreciation Method', viewAsset.depreciation_method.replace('_', ' '))}
            {row('Age', `${viewAsset.age_years} years`)}
            {row('Accumulated Depreciation', fmtCur(viewAsset.accumulated_depreciation))}
            {row('Net Book Value', fmtCur(viewAsset.net_book_value), true)}
            {row('Annual Depreciation Charge', fmtCur(viewAsset.annual_depreciation))}
            {viewAsset.notes && <><SectionHead title="📝 Notes" /><div style={{ fontSize: 13, color: 'var(--textMuted)' }}>{viewAsset.notes}</div></>}
            <button style={{ ...btnSm, width: '100%', marginTop: 16 }} onClick={() => setViewAsset(null)}>Close</button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div style={{ background: 'var(--bgCard)', border: '1px solid #2a2a2a', borderRadius: 8, padding: 24, maxWidth: 660, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 15, marginBottom: 16 }}>
              {editAsset ? '✏️ Edit Asset' : '🏗️ Add New Asset'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <Field label="Asset Name *"><input style={inputStyle} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Industrial Dishwasher — Kitchen" /></Field>
              </div>
              <Field label="Asset Type *">
                <select style={inputStyle} value={form.asset_type} onChange={e => setForm(p => ({ ...p, asset_type: e.target.value }))}>
                  <option value="fixed">Fixed Asset</option>
                  <option value="current">Current Asset</option>
                </select>
              </Field>
              <Field label="Category *">
                <select style={inputStyle} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {ASSET_CATS.filter(c => !form.asset_type || c.type === form.asset_type).map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </Field>
              <Field label="Purchase Date *"><input type="date" style={inputStyle} value={form.purchase_date} onChange={e => setForm(p => ({ ...p, purchase_date: e.target.value }))} /></Field>
              <Field label="Purchase Cost (KES) *"><input type="number" min="0" style={inputStyle} value={form.purchase_cost} onChange={e => setForm(p => ({ ...p, purchase_cost: e.target.value }))} placeholder="0" /></Field>
              <Field label="Location / Department"><input style={inputStyle} value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Main Kitchen, Room 201" /></Field>
              <Field label="Serial Number"><input style={inputStyle} value={form.serial_number} onChange={e => setForm(p => ({ ...p, serial_number: e.target.value }))} /></Field>
              <Field label="Supplier"><input style={inputStyle} value={form.supplier} onChange={e => setForm(p => ({ ...p, supplier: e.target.value }))} /></Field>
              <Field label="Invoice Number"><input style={inputStyle} value={form.invoice_number} onChange={e => setForm(p => ({ ...p, invoice_number: e.target.value }))} /></Field>
              <Field label="Status">
                <select style={inputStyle} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  {ASSET_STATUS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </Field>

              <div style={{ gridColumn: '1/-1', background: '#0d0d0d', borderRadius: 6, padding: 14 }}>
                <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 12, marginBottom: 10 }}>📉 DEPRECIATION</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field label="Method">
                    <select style={inputStyle} value={form.depreciation_method} onChange={e => setForm(p => ({ ...p, depreciation_method: e.target.value }))}>
                      {DEP_METHODS.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
                    </select>
                  </Field>
                  <Field label="Useful Life (years)">
                    <input type="number" min="0" style={inputStyle} value={form.useful_life_years} onChange={e => setForm(p => ({ ...p, useful_life_years: e.target.value }))} />
                  </Field>
                  <Field label="Salvage / Residual Value (KES)">
                    <input type="number" min="0" style={inputStyle} value={form.salvage_value} onChange={e => setForm(p => ({ ...p, salvage_value: e.target.value }))} />
                  </Field>
                  {form.depreciation_method === 'reducing_balance' && (
                    <Field label="Annual Rate (%)">
                      <input type="number" min="0" max="100" style={inputStyle} value={form.depreciation_rate} onChange={e => setForm(p => ({ ...p, depreciation_rate: e.target.value }))} />
                    </Field>
                  )}
                </div>
              </div>

              <Field label="Warranty Expiry"><input type="date" style={inputStyle} value={form.warranty_expiry} onChange={e => setForm(p => ({ ...p, warranty_expiry: e.target.value }))} /></Field>
              <Field label="Insurance Policy No."><input style={inputStyle} value={form.insurance_policy} onChange={e => setForm(p => ({ ...p, insurance_policy: e.target.value }))} /></Field>
              <div style={{ gridColumn: '1/-1' }}>
                <Field label="Notes"><textarea style={{ ...inputStyle, height: 60 }} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></Field>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : '💾 Save Asset'}</button>
              <button style={btnSm} onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   HOTEL REPORTS TAB
   ═══════════════════════════════════════════════════════════════════════════════ */

const REPORT_SECTIONS = [
  {
    id: 'daily',
    icon: '📅',
    title: 'Hotel Daily Reports',
    subtitle: 'The KPIs hotel managers check every day',
    reports: [
      { id: 'drr',       name: 'Daily Revenue Report (DRR)',     desc: 'Total revenue per day — rooms, food, bar, pool, conference' },
      { id: 'occupancy', name: 'Occupancy Report',               desc: 'Occupancy rate, rooms available vs occupied' },
      { id: 'adr',       name: 'Average Daily Rate (ADR)',        desc: 'Average room price for the period' },
      { id: 'revpar',    name: 'RevPAR',                          desc: 'Revenue per available room — most important hotel KPI' },
      { id: 'room_sales',name: 'Room Sales Report',              desc: 'Bookings, check-ins, check-outs, walk-ins' },
    ],
  },
  {
    id: 'operations',
    icon: '💳',
    title: 'Operations & Transactions',
    reports: [
      { id: 'invoices',    name: 'Invoices (Guest Bills)',    desc: 'All guest bills for the period' },
      { id: 'receipts',    name: 'Receipts (Payments Made)',  desc: 'All confirmed payment receipts' },
      { id: 'payment_rpt', name: 'Payment Methods Report',   desc: 'Breakdown by Cash, M-Pesa, PDQ, Bank, Room Charge' },
      { id: 'receivables', name: 'Accounts Receivable',      desc: 'Outstanding unpaid guest bills and folio balances' },
      { id: 'payables',    name: 'Accounts Payable',         desc: 'Pending supplier invoices and shopping lists' },
    ],
  },
  {
    id: 'fnb',
    icon: '🍽️',
    title: 'Food & Inventory',
    reports: [
      { id: 'inventory',   name: 'Inventory Report',   desc: 'Current stock levels, low-stock alerts, total value' },
      { id: 'stock_ledger',name: 'Stock Ledger',       desc: 'All stock movements — receipts, issuances, write-offs' },
      { id: 'cogs',        name: 'Cost of Goods Sold (COGS)', desc: 'Food & beverage cost vs revenue — restaurant profit' },
      { id: 'spoilage_rpt',name: 'Spoilage Report',   desc: 'Approved spoilage / waste records and their cost impact' },
    ],
  },
  {
    id: 'payroll',
    icon: '👨‍💼',
    title: 'Payroll & Staff',
    reports: [
      { id: 'payroll_rpt', name: 'Payroll Report',       desc: 'All staff salaries and deductions for the period' },
      { id: 'payslips',    name: 'Payslips',              desc: 'Individual payslips for each staff member' },
      { id: 'staff_cost',  name: 'Staff Cost Report',     desc: 'Total labour cost as % of revenue' },
    ],
  },
  {
    id: 'management',
    icon: '📊',
    title: 'Management Reports',
    reports: [
      { id: 'expense_rpt',   name: 'Expense Report',                  desc: 'All approved expenses by category' },
      { id: 'dept_profit',   name: 'Profitability by Department',     desc: 'Rooms vs Restaurant vs Events vs Pool' },
      { id: 'budget_actual', name: 'Budget vs Actual',                desc: 'Revenue and expense vs budget targets' },
      { id: 'breakeven',     name: 'Break-even Analysis',             desc: 'Fixed costs, variable costs, break-even revenue point' },
    ],
  },
  {
    id: 'bank_tax',
    icon: '🏦',
    title: 'Bank & Tax',
    reports: [
      { id: 'bank_stmt',  name: 'Bank Statement Summary',   desc: 'Cash inflows and outflows for the period' },
      { id: 'bank_recon', name: 'Bank Reconciliation',      desc: 'System revenue vs bank balance reconciliation' },
      { id: 'vat_rpt',    name: 'VAT Report (KRA)',          desc: 'Output VAT collected — file via iTax by 20th' },
      { id: 'paye_rpt',   name: 'PAYE Report',              desc: 'Pay As You Earn deductions for KRA filing' },
    ],
  },
];

export function HotelReportsTab({ notify, period, dateFrom, dateTo, setPeriod, setDateFrom, setDateTo }) {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [exporting,  setExporting]  = useState('');
  const [activeRpt,  setActiveRpt]  = useState(null);

  const PERIODS = [
    { id: 'day', label: 'Today' }, { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' }, { id: 'year', label: 'This Year' },
    { id: 'custom', label: 'Custom Range' },
  ];

  const buildParams = useCallback(() => {
    let p = `?period=${period}`;
    if (period === 'custom' && dateFrom && dateTo) p += `&date_from=${dateFrom}&date_to=${dateTo}`;
    return p;
  }, [period, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getAccountantReports(buildParams());
      setData(r.data);
    } catch (e) { notify('Failed to load report data', 'error'); }
    setLoading(false);
  }, [buildParams, notify]);

  useEffect(() => { load(); }, [load]);

  const doExport = (type) => {
    setExporting(type);
    const params = buildParams();
    const url = type === 'pdf'
      ? api.exportAccountantPdf(params)
      : api.exportAccountantExcel(params);
    doDownload(url);
    setTimeout(() => setExporting(''), 4000);
  };

  const fmtN = (v) => parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const fmtP = (v) => `${parseFloat(v || 0).toFixed(1)}%`;

  // Build computed report data from the main data object
  const buildReportData = (reportId) => {
    if (!data) return null;
    const { revenue: rev, rooms, guests, payment_methods: pm, expenses: exp, daily_trend } = data;
    switch (reportId) {
      case 'drr': return {
        title: 'Daily Revenue Report', sections: [
          { label: 'Total Revenue', value: `KES ${fmtN(rev.total)}`, highlight: true },
          { label: 'Room Revenue', value: `KES ${fmtN(rev.room)}` },
          { label: 'Food & Restaurant', value: `KES ${fmtN(rev.food)}` },
          { label: 'Bar & Beverages', value: `KES ${fmtN(rev.bar)}` },
          { label: 'Pool Revenue', value: `KES ${fmtN(rev.pool)}` },
          { label: 'Conference Revenue', value: `KES ${fmtN(rev.conference)}` },
          { label: 'Donations', value: `KES ${fmtN(rev.donations)}` },
          { label: 'VAT Collected (16%)', value: `KES ${fmtN(rev.vat_collected)}`, sub: true },
          { label: 'Net Revenue (ex-VAT)', value: `KES ${fmtN(rev.net_ex_vat)}`, highlight: true },
        ]
      };
      case 'occupancy': return {
        title: 'Occupancy Report', sections: [
          { label: 'Occupancy Rate', value: fmtP(rooms.occupancy_rate), highlight: true },
          { label: 'Total Rooms', value: rooms.total },
          { label: 'Occupied', value: rooms.occupied },
          { label: 'Available', value: rooms.available },
          { label: 'Guests Checked In (period)', value: guests.checked_in },
          { label: 'Guests Checked Out (period)', value: guests.checked_out },
          { label: 'Currently In-House', value: guests.active },
        ]
      };
      case 'adr': return {
        title: 'Average Daily Rate (ADR)', sections: [
          { label: 'ADR', value: `KES ${fmtN(rooms.adr)}`, highlight: true },
          { label: 'Room Revenue', value: `KES ${fmtN(rev.room)}` },
          { label: 'Rooms Sold (period)', value: rooms.occupied },
          { label: 'Industry benchmark (budget hotel)', value: 'KES 3,000–5,000' },
          { label: 'Industry benchmark (mid-range)', value: 'KES 7,000–15,000' },
        ]
      };
      case 'revpar': return {
        title: 'Revenue Per Available Room (RevPAR)', sections: [
          { label: 'RevPAR', value: `KES ${fmtN(rooms.revpar)}`, highlight: true },
          { label: 'Room Revenue', value: `KES ${fmtN(rev.room)}` },
          { label: 'Total Available Rooms', value: rooms.total },
          { label: 'ADR', value: `KES ${fmtN(rooms.adr)}` },
          { label: 'Occupancy Rate', value: fmtP(rooms.occupancy_rate) },
          { label: 'Formula', value: 'ADR × Occupancy Rate', sub: true },
        ]
      };
      case 'receivables': return {
        title: 'Accounts Receivable', sections: [
          { label: 'Outstanding Folio Balances', value: `KES ${fmtN(data.outstanding_folios)}`, highlight: true },
          { label: 'Active In-House Guests', value: guests.active },
          { label: 'Period Checked-In', value: guests.checked_in },
        ]
      };
      case 'payment_rpt': return {
        title: 'Payment Methods Report', sections: [
          { label: 'Cash', value: `KES ${fmtN(pm.cash)}` },
          { label: 'M-Pesa', value: `KES ${fmtN(pm.mpesa)}` },
          { label: 'Bank Transfer', value: `KES ${fmtN(pm.bank)}` },
          { label: 'PDQ / Card', value: `KES ${fmtN(pm.pdq)}` },
          { label: 'Room Charge', value: `KES ${fmtN(pm.room_charge)}` },
          { label: 'Total', value: `KES ${fmtN(Object.values(pm).reduce((s, v) => s + v, 0))}`, highlight: true },
        ]
      };
      case 'expense_rpt': return {
        title: 'Expense Report', sections: [
          { label: 'Total Expenses', value: `KES ${fmtN(exp.total)}`, highlight: true },
          { label: 'Net Profit', value: `KES ${fmtN(exp.net_profit)}`, highlight: true },
          ...(exp.by_category || []).map(c => ({ label: c.label, value: `KES ${fmtN(c.amount)}` })),
        ]
      };
      case 'vat_rpt': return {
        title: 'VAT Report (Kenya Revenue Authority)', sections: [
          { label: 'Gross Revenue (VAT Inclusive)', value: `KES ${fmtN(rev.total)}` },
          { label: 'Net Revenue (VAT Exclusive)', value: `KES ${fmtN(rev.net_ex_vat)}` },
          { label: 'Output VAT Collected', value: `KES ${fmtN(rev.vat_collected)}`, highlight: true },
          { label: 'VAT Rate', value: '16% (Kenya standard)' },
          { label: 'Filing Deadline', value: '20th of the following month' },
          { label: 'Filing Platform', value: 'KRA iTax — itax.kra.go.ke', sub: true },
        ]
      };
      case 'dept_profit': return {
        title: 'Profitability by Department', sections: [
          { label: 'Rooms Revenue', value: `KES ${fmtN(rev.room)}`, highlight: true },
          { label: 'F&B (Food + Bar)', value: `KES ${fmtN(parseFloat(rev.food) + parseFloat(rev.bar))}` },
          { label: 'Pool Revenue', value: `KES ${fmtN(rev.pool)}` },
          { label: 'Conference Revenue', value: `KES ${fmtN(rev.conference)}` },
          { label: 'Total Expenses', value: `KES ${fmtN(exp.total)}` },
          { label: 'Net Profit', value: `KES ${fmtN(exp.net_profit)}`, highlight: exp.net_profit >= 0 },
        ]
      };
      case 'breakeven': {
        const total_rev = parseFloat(rev.total) || 1;
        const total_exp = parseFloat(exp.total) || 0;
        const margin_pct = ((total_rev - total_exp) / total_rev * 100).toFixed(1);
        return {
          title: 'Break-even Analysis', sections: [
            { label: 'Total Revenue', value: `KES ${fmtN(total_rev)}` },
            { label: 'Total Expenses (Fixed + Variable)', value: `KES ${fmtN(total_exp)}` },
            { label: 'Net Margin', value: `${margin_pct}%`, highlight: true },
            { label: 'Status', value: total_exp <= total_rev ? '✅ Above break-even' : '⚠️ Below break-even — loss period', highlight: true },
            { label: 'Profit/Loss', value: `KES ${fmtN(exp.net_profit)}` },
          ]
        };
      }
      default: return null;
    }
  };

  const sectionBg = { background: 'var(--bgCard)', border: '1px solid #1e1e1e', borderRadius: 6, padding: 14, marginBottom: 10 };
  const rptBtnSt  = (isActive) => ({
    background: isActive ? '#1a0d00' : 'transparent',
    border: `1px solid ${isActive ? '#c8a96e' : '#2a2a2a'}`,
    borderRadius: 6, padding: '8px 12px', cursor: 'pointer', textAlign: 'left', width: '100%',
    color: isActive ? '#c8a96e' : 'var(--text)', marginBottom: 4, transition: 'all 0.15s',
  });

  return (
    <div>
      {/* Period selector */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16, background: 'var(--bgCard)', padding: 12, borderRadius: 6, border: '1px solid #1e1e1e' }}>
        <span style={{ fontSize: 12, color: 'var(--textMuted)', marginRight: 4 }}>Period:</span>
        {PERIODS.map(p => (
          <button key={p.id} style={{ ...btnSm, background: period === p.id ? '#c8a96e' : 'transparent', color: period === p.id ? '#000' : 'var(--textMuted)', border: '1px solid #333' }}
            onClick={() => setPeriod(p.id)}>{p.label}</button>
        ))}
        {period === 'custom' && <>
          <input type="date" style={{ ...inputStyle, padding: '5px 8px', fontSize: 12 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <span style={{ color: 'var(--textFaint)' }}>→</span>
          <input type="date" style={{ ...inputStyle, padding: '5px 8px', fontSize: 12 }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </>}
        <button style={{ ...btnSm, marginLeft: 'auto', background: '#1a3a6b' }} onClick={load} disabled={loading}>{loading ? '⏳' : '🔄 Refresh'}</button>
        <button style={{ ...btnSm, background: '#27ae60' }} onClick={() => doExport('excel')} disabled={!!exporting}>{exporting === 'excel' ? '⏳…' : '📊 Excel'}</button>
        <button style={{ ...btnSm, background: '#8B2635' }} onClick={() => doExport('pdf')} disabled={!!exporting}>{exporting === 'pdf' ? '⏳…' : '📄 Full PDF Report'}</button>
      </div>

      {loading ? <div style={{ color: 'var(--textMuted)', padding: 40, textAlign: 'center' }}>Loading report data…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16 }}>
          {/* Left: Report Menu */}
          <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            {REPORT_SECTIONS.map(section => (
              <div key={section.id} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 12, marginBottom: 6, letterSpacing: 0.5 }}>{section.icon} {section.title}</div>
                {section.reports.map(rpt => (
                  <button key={rpt.id} style={rptBtnSt(activeRpt === rpt.id)} onClick={() => setActiveRpt(rpt.id)}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{rpt.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--textFaint)', marginTop: 2 }}>{rpt.desc}</div>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Right: Report Content */}
          <div>
            {!activeRpt ? (
              <div style={{ ...sectionBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: 'var(--textMuted)' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📑</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Select a Report</div>
                <div style={{ fontSize: 13 }}>Choose any report from the left panel to view data</div>
              </div>
            ) : (() => {
              const rptData = buildReportData(activeRpt);
              const allRpts = REPORT_SECTIONS.flatMap(s => s.reports);
              const rptMeta = allRpts.find(r => r.id === activeRpt);
              if (!rptData) return (
                <div style={{ ...sectionBg }}>
                  <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 15, marginBottom: 8 }}>{rptMeta?.name}</div>
                  <div style={{ color: 'var(--textMuted)', fontSize: 13, padding: 20, textAlign: 'center' }}>
                    This report requires data not currently available in the dashboard. Use the <strong>📊 Excel</strong> or <strong>📄 PDF</strong> export for a full report.
                  </div>
                </div>
              );
              return (
                <div style={sectionBg}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 15 }}>{rptData.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--textMuted)', marginTop: 2 }}>
                        Period: {data.period.start} → {data.period.end}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...btnSm, background: '#27ae60', fontSize: 11 }} onClick={() => doExport('excel')} disabled={!!exporting}>📊 Excel</button>
                      <button style={{ ...btnSm, background: '#8B2635', fontSize: 11 }} onClick={() => doExport('pdf')} disabled={!!exporting}>📄 PDF</button>
                    </div>
                  </div>
                  {rptData.sections.map((s, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '10px 12px',
                      background: s.highlight ? '#0d1a0d' : s.sub ? '#0a0a0a' : 'transparent',
                      borderBottom: '1px solid #1e1e1e', borderRadius: s.highlight ? 4 : 0,
                      marginBottom: s.highlight ? 4 : 0,
                    }}>
                      <span style={{ color: s.highlight ? 'var(--text)' : 'var(--textMuted)', fontSize: 13, fontWeight: s.highlight ? 600 : 400 }}>{s.label}</span>
                      <span style={{ fontWeight: s.highlight ? 800 : 600, color: s.highlight ? '#2ecc71' : '#c8a96e', fontSize: s.highlight ? 15 : 13 }}>{s.value}</span>
                    </div>
                  ))}

                  {/* Show daily trend mini chart for DRR */}
                  {activeRpt === 'drr' && data.daily_trend && data.daily_trend.length > 1 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontWeight: 600, color: '#c8a96e', fontSize: 12, marginBottom: 8 }}>📈 Daily Revenue Trend</div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ background: '#0d0d0d' }}>
                              <th style={{ padding: '4px 8px', color: '#c8a96e', textAlign: 'left' }}>Date</th>
                              <th style={{ padding: '4px 8px', color: '#c8a96e', textAlign: 'right' }}>Revenue (KES)</th>
                              <th style={{ padding: '4px 8px', color: '#c8a96e', textAlign: 'right' }}>Expenses (KES)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.daily_trend.slice(-14).map((d, i) => (
                              <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#0a0a0a' }}>
                                <td style={{ padding: '4px 8px' }}>{d.date}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'right', color: '#2ecc71' }}>{parseFloat(d.revenue).toLocaleString()}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'right', color: '#e74c3c' }}>{parseFloat(d.expense || 0).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   SPOILAGE REVIEW TAB (Accountant view)
   ═══════════════════════════════════════════════════════════════════════════════ */

const ST_COLOR = { draft: '#888', submitted: '#f39c12', approved: '#2ecc71', rejected: '#e74c3c' };

export function SpoilageReviewTab({ notify }) {
  const [reports,      setReports]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filterStatus, setFilterStatus] = useState('submitted');
  const [rejectModal,  setRejectModal]  = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [summary,      setSummary]      = useState(null);
  const [exporting,    setExporting]    = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let params = filterStatus ? `?status=${filterStatus}` : '?';
      if (dateFrom) params += `&date_from=${dateFrom}`;
      if (dateTo)   params += `&date_to=${dateTo}`;
      const [r, s] = await Promise.all([api.getSpoilageReports(params), api.getSpoilageSummary()]);
      setReports(r.data.results || r.data);
      setSummary(s.data);
    } catch (e) { notify('Failed to load spoilage reports', 'error'); }
    setLoading(false);
  }, [filterStatus, dateFrom, dateTo, notify]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.approveSpoilageReport(id); notify('✅ Approved — expense posted to Food & Beverage costs', 'success'); load(); }
    catch (e) { notify('Approval failed', 'error'); }
  };

  const doReject = async () => {
    if (!rejectReason) { notify('Please provide a reason', 'error'); return; }
    try { await api.rejectSpoilageReport(rejectModal, { reason: rejectReason }); notify('Report rejected', 'info'); setRejectModal(null); setRejectReason(''); load(); }
    catch (e) { notify('Reject failed', 'error'); }
  };

  const doExport = async (type) => {
    setExporting(type);
    notify(`Preparing ${type.toUpperCase()} export…`);
    try {
      let params = '?';
      if (filterStatus) params += `status=${filterStatus}&`;
      if (dateFrom)     params += `date_from=${dateFrom}&`;
      if (dateTo)       params += `date_to=${dateTo}&`;
      const url = type === 'excel'
        ? api.exportSpoilageExcel(params)
        : api.exportSpoilagePdf(params);
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '' });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_SpoilageReport_${new Date().toISOString().slice(0,10)}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      notify(`Spoilage report exported ✓`);
    } catch (e) { notify(`Export failed: ${await readBlobError(e)}`, 'error'); }
    setExporting('');
  };

  const cardSt = { background: 'var(--bgCard)', border: '1px solid #1e1e1e', borderRadius: 6, padding: 14, marginBottom: 10 };
  const tagSt  = (st) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: ST_COLOR[st] || '#888', background: 'rgba(0,0,0,0.3)', border: `1px solid ${ST_COLOR[st] || '#555'}`, marginLeft: 8 });

  return (
    <div>
      {summary && (
        <StatCards stats={[
          { label: 'Pending Approval', value: summary.pending_count || 0,                           color: '#f39c12', icon: '⏳' },
          { label: 'This Month (Approved)', value: `KES ${(summary.this_month || 0).toLocaleString()}`, color: '#e74c3c', icon: '🗑️' },
          { label: 'This Year (Total Waste)', value: `KES ${(summary.this_year || 0).toLocaleString()}`, color: '#e67e22', icon: '📅' },
        ]} />
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {['', 'draft', 'submitted', 'approved', 'rejected'].map(s => (
          <button key={s} style={{ ...btnSm, background: filterStatus === s ? '#c8a96e' : 'transparent', color: filterStatus === s ? '#000' : 'var(--textMuted)', border: '1px solid #333' }}
            onClick={() => setFilterStatus(s)}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}</button>
        ))}
        <div style={{ flex: 1 }} />
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          style={{ ...inputStyle, width: 140, fontSize: 11 }} title="Date from" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          style={{ ...inputStyle, width: 140, fontSize: 11 }} title="Date to" />
        <button style={{ ...btnSm, background: '#27ae60', color: '#fff', fontWeight: 700 }}
          onClick={() => doExport('excel')} disabled={!!exporting} title="Export to Excel">
          {exporting === 'excel' ? '⏳…' : '📊 Excel'}
        </button>
        <button style={{ ...btnSm, background: '#8B2635', color: '#fff', fontWeight: 700 }}
          onClick={() => doExport('pdf')} disabled={!!exporting} title="Export to PDF">
          {exporting === 'pdf' ? '⏳…' : '📄 PDF'}
        </button>
      </div>

      {loading ? <div style={{ color: 'var(--textMuted)', padding: 24, textAlign: 'center' }}>Loading…</div>
        : reports.length === 0 ? <Empty text={filterStatus === 'submitted' ? 'No reports pending approval' : 'No spoilage reports found'} />
        : reports.map(r => (
          <div key={r.id} style={{ ...cardSt, borderLeft: `3px solid ${ST_COLOR[r.status] || '#333'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <span style={{ fontWeight: 700, color: '#c8a96e' }}>{r.ref}</span>
                <span style={tagSt(r.status)}>{r.status_label || r.status}</span>
                <div style={{ fontWeight: 600, marginTop: 4 }}>{r.title}</div>
                <div style={{ fontSize: 12, color: 'var(--textMuted)', marginTop: 2 }}>
                  {r.category_label} · {r.date_occurred} · Chef: {r.reported_by_name || '—'}
                </div>
                {r.description && <div style={{ fontSize: 12, color: 'var(--textFaint)', marginTop: 4 }}>{r.description}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#e74c3c' }}>
                  KES {parseFloat(r.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 11, color: 'var(--textMuted)' }}>Total waste value</div>
              </div>
            </div>

            {/* Items table */}
            {r.items && r.items.length > 0 && (
              <div style={{ marginTop: 12, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#0d0d0d' }}>
                      {['Item', 'Qty', 'Unit', 'Unit Value', 'Total Value', 'Notes'].map(h => (
                        <th key={h} style={{ padding: '4px 8px', textAlign: 'left', color: '#c8a96e', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {r.items.map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #1a1a1a' }}>
                        <td style={{ padding: '4px 8px', fontWeight: 600 }}>{it.item_name}</td>
                        <td style={{ padding: '4px 8px' }}>{it.quantity}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--textMuted)' }}>{it.unit}</td>
                        <td style={{ padding: '4px 8px' }}>KES {parseFloat(it.unit_value).toLocaleString()}</td>
                        <td style={{ padding: '4px 8px', fontWeight: 700, color: '#e74c3c' }}>KES {parseFloat(it.total_value).toLocaleString()}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--textFaint)' }}>{it.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {r.preventive_action && (
              <div style={{ marginTop: 10, fontSize: 12, background: '#0a1a0a', padding: '8px 12px', borderRadius: 4, borderLeft: '3px solid #2ecc71' }}>
                <span style={{ fontWeight: 700, color: '#2ecc71' }}>Preventive Action: </span>
                <span style={{ color: 'var(--textMuted)' }}>{r.preventive_action}</span>
              </div>
            )}

            {r.status === 'submitted' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button style={{ ...btnPrimary, background: '#1a6b3a', fontSize: 12 }} onClick={() => approve(r.id)}>
                  ✅ Approve — Post KES {parseFloat(r.total_value).toLocaleString()} to Expenses
                </button>
                <button style={{ ...btnSm, background: '#5a1010' }} onClick={() => { setRejectModal(r.id); setRejectReason(''); }}>
                  ❌ Reject
                </button>
              </div>
            )}
            {r.status === 'approved' && (
              <div style={{ marginTop: 12 }}>
                <div style={{
                  display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap',
                  padding: '10px 12px',
                  background: 'rgba(200,169,110,0.05)',
                  border: '1px solid rgba(200,169,110,0.13)',
                  borderRadius: 6, marginBottom: 8,
                }}>
                  <div style={{ width: '100%', fontSize: 9, color: '#555', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>
                    Official Approval Stamp
                  </div>
                  <SpoilageAccountantStamp
                    by={r.approved_by_name || null}
                    at={r.approved_at}
                    size={72}
                  />
                </div>
                <div style={{ fontSize: 12, color: '#2ecc71' }}>
                  ✅ Approved by {r.approved_by_name || '—'} — Posted to Food &amp; Beverage expenses
                </div>
              </div>
            )}
            {r.status === 'rejected' && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#e74c3c' }}>
                ❌ Rejected: {r.rejection_reason || 'No reason given'}
              </div>
            )}
          </div>
        ))}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--bgCard)', border: '1px solid #2a2a2a', borderRadius: 8, padding: 24, maxWidth: 400, width: '100%' }}>
            <div style={{ fontWeight: 700, color: '#e74c3c', marginBottom: 12 }}>❌ Reject Spoilage Report</div>
            <Field label="Reason for rejection">
              <textarea style={{ ...inputStyle, height: 80 }} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Explain why…" />
            </Field>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button style={{ ...btnPrimary, background: '#8B2635' }} onClick={doReject}>Reject</button>
              <button style={btnSm} onClick={() => setRejectModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}